import {
  useMutateSaveChat,
  useMutateUnsaveChat,
} from '@/hooks/mutation/useMutateSaveChat';
import { useQueryChats } from '@/hooks/query/useQueryChats';
import { useQuerySavedChats } from '@/hooks/query/useQuerySavedChats';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import type { ChatListItem } from '@/types';
import { filterChatsForWorkAvailability } from '@heidi/agents/contracts';
import Fuse from 'fuse.js';
import { useCallback, useMemo } from 'react';

/**
 * Hook to manage saved (favorited) chats.
 *
 * Backed by backend API endpoints:
 * - GET  /chat/chats/saved        — list saved chats
 * - POST /chat/chats/{id}/save    — save a chat
 * - DELETE /chat/chats/{id}/save  — unsave a chat
 *
 * Returns no-ops for unauthenticated (anonymous/temp) users.
 */
export function useSavedChats() {
  const { isAuthenticated } = useIsAuthenticated();

  const { data: savedChatsData, isPending: savedChatsPending } = useQuerySavedChats();
  const { data: chatsData } = useQueryChats(1, 100);
  const saveMutation = useMutateSaveChat();
  const unsaveMutation = useMutateUnsaveChat();

  /** Full ChatListItem objects for saved chats from the backend. */
  const savedChats = useMemo<ChatListItem[]>(() => {
    if (!isAuthenticated) return [];
    return savedChatsData?.chats ?? [];
  }, [isAuthenticated, savedChatsData?.chats]);

  /** Check if a chat is saved using the is_saved field from the chat list. */
  const isChatSaved = useCallback(
    (id: string) => {
      if (!isAuthenticated) return false;
      // Check from the main chats list (has is_saved from backend)
      const chat = chatsData?.chats.find((c) => c.id === id);
      if (chat) return chat.is_saved;
      // Fallback: check if it's in the saved chats list
      return savedChats.some((c) => c.id === id);
    },
    [isAuthenticated, chatsData?.chats, savedChats]
  );

  const saveChat = useCallback(
    (id: string) => {
      if (!isAuthenticated) return;
      saveMutation.mutate(id);
    },
    [isAuthenticated, saveMutation]
  );

  const unsaveChat = useCallback(
    (id: string) => {
      if (!isAuthenticated) return;
      unsaveMutation.mutate(id);
    },
    [isAuthenticated, unsaveMutation]
  );

  const toggleSaveChat = useCallback(
    (id: string) => {
      if (!isAuthenticated) return;
      if (isChatSaved(id)) {
        unsaveChat(id);
      } else {
        saveChat(id);
      }
    },
    [isAuthenticated, isChatSaved, saveChat, unsaveChat]
  );

  return {
    savedChats,
    savedChatsPending,
    isChatSaved,
    saveChat,
    unsaveChat,
    toggleSaveChat,
  };
}

export function useFilteredSavedChats(searchQuery: string, isWorkEnabled: boolean) {
  const { savedChats, savedChatsPending } = useSavedChats();

  const visibleSavedChats = useMemo(
    () => filterChatsForWorkAvailability(savedChats, isWorkEnabled),
    [savedChats, isWorkEnabled]
  );

  const filteredSavedChats = useMemo(() => {
    if (!searchQuery.trim()) return visibleSavedChats;
    const savedFuse = new Fuse(visibleSavedChats, { keys: ['title'], threshold: 0.3 });
    return savedFuse.search(searchQuery).map((result) => result.item);
  }, [visibleSavedChats, searchQuery]);

  return { visibleSavedChats, filteredSavedChats, savedChatsPending };
}
